import Layout, { Stack } from '@/components/Layout'
import MenuLinkInternal from '@/components/MenuLinkInternal'
import MenuLink from '@/components/MenuLinks'
import ParentMenuItem from '@/components/ParentMenuItem'
import { rbp } from '@/constants/measured-scope'
import { useFeatureToggle } from '@/context/featureToggles'
import { SecondaryNavigation } from '@/shared/api-generated-types'
import AiSearchBox from '@/widgets/AiSearchBox'
import SearchBox from '@/widgets/SearchBox'
import { useMediaQuery } from '@react-hookz/web'
import { useRouter } from 'next/router'
import { useCallback, useEffect, useRef, useState } from 'react'
import { gql, useQuery } from 'urql'
import * as styles from './style'

const QUERY_SECONDARY_NAVIGATION = gql`
  query secondaryNavigation($partnerSlug: String) {
    secondaryNavigation(partnerSlug: $partnerSlug) {
      items {
        ... on NavMenuItemInternal {
          ...NavMenuItemInternal
        }
        ... on NavMenuItemParent {
          id
          label
          submenu {
            ...NavMenuItemInternal
          }
        }
      }
    }
  }

  fragment NavMenuItemInternal on NavMenuItemInternal {
    __typename
    id
    label
    active
    isExternal
    shouldBeAuthenticatedUser
    hideOnMobile
    isBeta
    isNew
    url
  }
`
const SecondaryNavigationComponent = (): JSX.Element => {
  const aiSearchBoxEnabled = useFeatureToggle('ai_search_box')
  const router = useRouter()
  const isLargeDevice = useMediaQuery(`only screen and (min-width : ${rbp.lg})`, { initializeWithValue: false })
  const [openDropdown, setOpenDropdown] = useState<number | null>(null) // State for dropdown visibility
  const dropdownRef = useRef<HTMLDivElement>(null) // Ref for dropdown container

  // Fetch secondary navigation from the backend
  const partnerSlug = router.route === '/partner/[slug]' ? router.query?.slug : undefined
  const [{ data, fetching }] = useQuery<{ secondaryNavigation: SecondaryNavigation }>({
    query: QUERY_SECONDARY_NAVIGATION,
    requestPolicy: 'cache-first',
    variables: { partnerSlug },
  })

  const isActive = useCallback(
    (url?: string): boolean => {
      if (!router.isReady) return false
      if (url === '/' && router.pathname === '/partner/[slug]') return true
      if (router.asPath === '/series/socialconnections') {
        return url === '/series/socialconnections' // Community button should be active when on community page
      }
      if (url === '/' && router.pathname === '/exp-home') return true
      if (url === '/' && router.pathname !== '/') return false
      return router.asPath === url || router.asPath.startsWith(`${url}/`)
    },
    [router.isReady, router.asPath],
  )

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setOpenDropdown(null)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  return (
    <Layout className={styles.wrapper}>
      <Stack className={styles.container} x alignItems="flex-start" justifyContent="space-between">
        <Stack className={styles.navigation} x>
          {fetching ? (
            <></>
          ) : (
            data?.secondaryNavigation?.items.map((menuLink, i) => {
              const isParentActive = 'submenu' in menuLink && menuLink.submenu.some((subLink) => isActive(subLink.url))
              // 2 cases here: either the menu will have an 'url' or 'submenu' in it
              if ('url' in menuLink) {
                if (menuLink.isExternal) {
                  return (
                    <MenuLink
                      key={`menu-link-external-${i}`}
                      hasPopOut={true}
                      label={menuLink.label}
                      url={menuLink.url}
                      openInNewTab={false}
                    />
                  )
                } else if (isLargeDevice || !menuLink.hideOnMobile) {
                  return (
                    <MenuLinkInternal
                      key={`menu-link-internal-${i}`}
                      active={isActive(menuLink.url)}
                      label={menuLink.label}
                      url={menuLink.url}
                      isBeta={menuLink.isBeta}
                      isNew={menuLink.isNew}
                    />
                  )
                }
              } else if ('submenu' in menuLink) {
                return (
                  <div
                    key={`menu-link-dropdown-${i}`}
                    className={styles.dropdown}
                    onClick={() => setOpenDropdown(openDropdown === i ? null : i)}
                    ref={openDropdown === i ? dropdownRef : null}
                  >
                    <ParentMenuItem active={isParentActive} label={menuLink.label} />
                    {openDropdown === i && (
                      <div className={styles.dropdownMenu}>
                        {menuLink.submenu.map((subLink, subIndex) => (
                          <MenuLinkInternal
                            key={`menu-link-submenu-${subIndex}`}
                            active={isActive(subLink.url)}
                            label={subLink.label}
                            url={subLink.url}
                            className={styles.dropdownMenuItem}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                )
              }
            })
          )}
        </Stack>
        {isLargeDevice && (
          <div className={styles.searchContainer}>
            {aiSearchBoxEnabled ? (
              !router.asPath.includes('/helen') && <AiSearchBox />
            ) : (
              <SearchBox hasResetOption isLargeDevice />
            )}
          </div>
        )}
      </Stack>
    </Layout>
  )
}
export default SecondaryNavigationComponent
